import { useLocation, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { useState } from "react";
import { NewProfileFrame } from "../NewProfileFrame";
import { H2, H3 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { Button } from "../../../components/Button";
import { Box } from "../../../components/Box";
import { Card } from "../../../components/Card";
import { Flex } from "../../../components/Flex";
import { Modal } from "../../../components/Modal";
import { ROUTE } from "../../../routes";
import { useCreateProfile } from "../../../data/useCreateProfile";
import { AddressPayload, CreateProfileRequest } from "../../../types/Profile";
import { useGetAddressTypeList } from "../../../data/useGetAddressTypeList";
import { usePatchProfile } from "../../../data/usePatchProfile";
import { showError } from "../../../utils/error-handling";
import { useUserDetails } from "../../../data/useUserDetails";
import { useAuth } from "../../../context/AuthContext";

const Header = () => (
  <>
    <H2>Person Summary</H2>
    <Paragraph>
      Please check the details of this person before creating.
    </Paragraph>
  </>
);

type LocationState = {
  newIndividual?: {
    division?: number | string;
    team?: number | string;
    first_name: string;
    middle_name?: string | null;
    last_name: string;
    date_of_birth?: string | null;
    email?: string | null;
    gender?: string | null;
    title?: string | null;
    yourReference?: string | null;
  };
  residentialAddress?: {
    flat_appartment_subbuilding?: string;
    building_number?: string;
    building_name?: string;
    road_street?: string;
    town_city?: string;
    district?: string;
    state_province_name?: string;
    state_province_code?: string;
    post_zip_code?: string;
    country_code?: string;
    country_name?: string;
  };
  existingIndividualId?: number;
  existingIndividual?: any;
};

export const NewPersonSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, authHeader, hasPermission } = useAuth();
  const {
    newIndividual,
    residentialAddress,
    existingIndividualId,
    existingIndividual,
  } = (location.state || {}) as LocationState;
  const { mutate: patchProfile } =
    usePatchProfile();
    const { data: userDetails } = useUserDetails(
      isLoggedIn,
      authHeader,
    );
  const { mutate: createProfile, isLoading } = useCreateProfile();
  const { data: addressTypes } = useGetAddressTypeList("all");

  const [showSuccess, setShowSuccess] = useState(false);

  const primaryResidenceId = addressTypes?.find(
    (t: any) => t.name === "Primary Residence"
  )?.id;

  const handleBack = () => {
    if (newIndividual) {
      navigate(ROUTE.PROFILE_NEW_PERSON_ADDRESS, {
        state: { newIndividual, residentialAddress },
      });
    } else {
      navigate(ROUTE.PROFILE_NEW_PERSON);
    }
  };

  const handleCreate = () => {
    if (existingIndividualId && !newIndividual) {
      navigate(`/profile/${existingIndividualId}`);
      return;
    }

    if (!newIndividual) return;

    const dob =
      newIndividual.date_of_birth &&
      DateTime.fromISO(newIndividual.date_of_birth).toFormat("yyyy-MM-dd");

    const addressPayload: AddressPayload[] = [];

    if (residentialAddress) {
      const cc = residentialAddress.country_code ?? null;

      addressPayload.push({
        _type: primaryResidenceId ?? undefined,
        country: cc,
        country_code: cc,
        building_name: residentialAddress.building_name ?? "",
        building_number: residentialAddress.building_number ?? "",
        flat_appartment_subbuilding:
          residentialAddress.flat_appartment_subbuilding ?? "",
        road_street: residentialAddress.road_street ?? "",
        line1: "",
        line2: "",
        district: residentialAddress.district ?? "",
        town_city: residentialAddress.town_city ?? "",
        state: "",
        state_province_code: residentialAddress.state_province_code ?? "",
        state_province_name: residentialAddress.state_province_name ?? "",
        post_zip_code: residentialAddress.post_zip_code ?? "",
        postcode: residentialAddress.post_zip_code ?? "",
      });
    }

    const fullName = [
      newIndividual.first_name,
      newIndividual.middle_name,
      newIndividual.last_name,
    ]
      .filter(Boolean)
      .join(" ");

    const payload: CreateProfileRequest = {
      _type: "individual",
      primary_division: newIndividual.division as number,
      primary_team: newIndividual.team as number,
      first_name: newIndividual.first_name,
      middle_name: newIndividual.middle_name ?? null,
      last_name: newIndividual.last_name,
      gender: newIndividual.gender,
      date_of_birth: dob || null,
      email: newIndividual.email ?? null,
      title: newIndividual.title ?? null,
      external_reference: newIndividual.yourReference || null,
      name: fullName || null,
      default: false,
      address: addressPayload,
      relation: [],
    };

    createProfile(payload, {
      onSuccess: (res: any) => {
        const id = res.data?.id ?? res.data?.data?.id;
        if (id) {
          const payload = {profileId: id, owner: userDetails?.data?.id ?? null };
          patchProfile(payload, {
            onSuccess: () => {
               setShowSuccess(true);
            },

            onError: (err) =>
              showError(
                err,
                `Error occurred while updating owner`,
              ),
          });
        } else {
          navigate(ROUTE.PROFILES);
        }
      },
      onError: (err) => {
        showError(err, "A problem occurred while creating the person profile.");
      },
    });
  };

  const displaySource = newIndividual ?? existingIndividual ?? null;

  const formattedDob =
    displaySource?.date_of_birth &&
    DateTime.fromISO(displaySource.date_of_birth).toFormat("dd/MM/yyyy");

  const existingAddress = existingIndividual?.address;

  const fullAddress = residentialAddress
    ? [
        residentialAddress.flat_appartment_subbuilding,
        residentialAddress.building_number,
        residentialAddress.building_name,
        residentialAddress.road_street,
        residentialAddress.town_city,
        residentialAddress.district,
        residentialAddress.post_zip_code,
      ]
        .filter(Boolean)
        .join(", ")
    : existingAddress
    ? [
        existingAddress.flat_appartment_subbuilding,
        existingAddress.building_number,
        existingAddress.building_name,
        existingAddress.road_street,
        existingAddress.town_city,
        existingAddress.district,
        existingAddress.post_zip_code,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  const fullName =
    displaySource &&
    (displaySource.name ||
      [
        displaySource.first_name,
        displaySource.middle_name,
        displaySource.last_name,
      ]
        .filter(Boolean)
        .join(" "));

  const countryLabel =
    residentialAddress?.country_name ||
    existingIndividual?.countryData?.name ||
    "";

  const navigateToNewPerson = () => {
    setShowSuccess(false);
    navigate(ROUTE.PROFILE_NEW_PERSON);
  };

  const navigateToProfiles = () => {
    setShowSuccess(false);
    navigate(ROUTE.PROFILES);
  };

  return (
    <NewProfileFrame mode="person" step="summary">
      <Header />

      <Box mt={3} maxWidth={650}>
        <Card p={3}>
          <Flex justifyContent="space-between" mb={2}>
            <Box>
              <H3 fontSize={2}>{fullName || "-"}</H3>
            </Box>
          </Flex>

          <Paragraph fontSize={1} color="gray.60" mb={1}>
            {formattedDob ? formattedDob : "-"}
          </Paragraph>

          <Paragraph fontSize={1} color="gray.60">
            {displaySource?.email || "-"}
          </Paragraph>

          {fullAddress || countryLabel ? (
            <>
              {fullAddress && (
                <Paragraph fontSize={1} color="gray.60" mt={2}>
                  {fullAddress}
                </Paragraph>
              )}

              {countryLabel && (
                <Paragraph fontSize={1} color="gray.60" mt={1}>
                  {countryLabel}
                </Paragraph>
              )}
            </>
          ) : (
            <Paragraph fontSize={1} color="gray.60" mt={2}>
              No address provided.
            </Paragraph>
          )}
        </Card>

        <Flex justifyContent="space-between" mt={3}>
          <Button variant="text" onClick={handleBack}>
            Back
          </Button>
          <Button
            onClick={handleCreate}
            isDisabled={isLoading || !hasPermission("profile.create")}
            title={
              hasPermission("profile.create")
                ? undefined
                : "You don't have permission to create profiles"
            }
          >
            {isLoading ? "Creating..." : "Create"}
          </Button>
        </Flex>
      </Box>

      <Modal
        isOpen={showSuccess}
        onClose={navigateToProfiles}
        title="Person created"
        content="Thank you for creating a new person profile. Please create another person or close to be taken to this record."
        onModalConfirmClick={navigateToNewPerson}
        secondaryButtonText="Close"
        primaryButtonText="+ New person"
        primaryButtonVariant="primary"
        hasSecondaryButton
        onSecondaryButtonClick={navigateToProfiles}
        width="450px"
      />
    </NewProfileFrame>
  );
};

export default NewPersonSummary;
